import React from 'react';
import { Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate, delta, downloadCsv } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, DataTable } from '../components/ui';

// Multi-location version of the POS end-of-day summary (SalesComposite):
// order counts & revenue by type, taxes, payments by tender, tips, discounts,
// refunds and failed orders.
export default function SalesSummary() {
  const { reportParams, startDate, endDate, compareMode } = useFilters();
  const { data, loading, error } = useReport('summary', { ...reportParams, compare: compareMode });

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Building sales summary…" />;

  const { current: c, previous: p, byOrderType, byPaymentType } = data;
  const totalTax = c.tax1 + c.tax2;

  const payBar = {
    labels: byPaymentType.map(r => r.paymentType),
    datasets: [{
      label: 'Amount (incl tax, before tips)',
      data: byPaymentType.map(r => r.amount),
      backgroundColor: SERIES[0],
      borderRadius: 4,
      maxBarThickness: 26,
    }],
  };

  // The KPI tiles as a CSV: one row per tile, dollars (not cents), with the
  // prior-period figure and % change where the tile shows one.
  const exportTilesCsv = () => {
    const pctChange = (curr, prev) => {
      const d = delta(curr, prev);
      return d ? (d.f * 100).toFixed(1) : '';
    };
    const dollars = v => ((v || 0) / 100).toFixed(2);
    const rows = [
      { metric: 'Net Sales', amount: dollars(c.netSales), previous: dollars(p.netSales), change: pctChange(c.netSales, p.netSales), note: 'before tax & tips' },
      { metric: 'Total + Tax', amount: dollars(c.grossSales), previous: dollars(p.grossSales), change: pctChange(c.grossSales, p.grossSales), note: 'collected excl. tips' },
      { metric: 'Tax', amount: dollars(totalTax), previous: '', change: '', note: 'Tax1 + Tax2' },
      { metric: 'Tax1', amount: dollars(c.tax1), previous: '', change: '', note: '' },
      { metric: 'Tax2', amount: dollars(c.tax2), previous: '', change: '', note: '' },
      { metric: 'Tips', amount: dollars(c.tips), previous: dollars(p.tips), change: pctChange(c.tips, p.tips), note: '' },
      { metric: 'Discounts', amount: dollars(c.discounts), previous: '', change: '', note: '' },
      { metric: 'Charges', amount: dollars(c.charges), previous: '', change: '', note: 'delivery fees, service charges' },
      { metric: 'Refunds', amount: dollars(c.refundAmount), previous: '', change: '', note: `${c.refundCount || 0} refunds` },
      { metric: 'Gift Cards Sold', amount: dollars(c.giftCardsSold), previous: '', change: '', note: `${c.giftCardCount || 0} sold · not in Net Sales` },
    ];
    downloadCsv(`sales-summary-${startDate}-to-${endDate}.csv`, [
      { key: 'metric', label: 'Metric' },
      { key: 'amount', label: 'Amount' },
      { key: 'previous', label: 'Prior Period' },
      { key: 'change', label: 'Change %' },
      { key: 'note', label: 'Note' },
    ], rows);
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card-head" style={{ marginBottom: -6 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>Summary</h3>
          <div className="sub" style={{ marginBottom: 0 }}>{niceDate(startDate)} – {niceDate(endDate)}</div>
        </div>
        <div className="spacer" />
        <div className="table-actions">
          <button className="btn-ghost" onClick={exportTilesCsv}>⬇ CSV</button>
        </div>
      </div>
      <div className="grid kpi-grid">
        <KpiCard label="Net Sales" value={money(c.netSales)} curr={c.netSales} prev={p.netSales} sub="before tax & tips" />
        <KpiCard label="Total + Tax" value={money(c.grossSales)} curr={c.grossSales} prev={p.grossSales} sub="collected excl. tips" />
        <KpiCard label="Tax" value={money(totalTax)} sub={`Tax1 ${money(c.tax1)} · Tax2 ${money(c.tax2)}`} />
        <KpiCard label="Tips" value={money(c.tips)} curr={c.tips} prev={p.tips} />
        <KpiCard label="Discounts" value={money(c.discounts)} sub={`charges ${money(c.charges)}`} invert />
        <KpiCard label="Refunds" value={money(c.refundAmount)} sub={`${int(c.refundCount)} refunds`} invert />
        <KpiCard label="Gift Cards Sold" value={money(c.giftCardsSold || 0)} sub={`${int(c.giftCardCount || 0)} sold · not in Net Sales`} />
      </div>

      <div className="grid two-col">
        <Card tour="sum-ordertypes" title="Orders by Type" sub={`${niceDate(startDate)} – ${niceDate(endDate)}`}>
          <DataTable
            csvName="orders-by-type.csv"
            defaultSort={{ key: 'netSales', dir: 'desc' }}
            columns={[
              { key: 'orderType', label: 'Order Type' },
              { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders), csv: r => r.orders, footer: rows => int(rows.reduce((a, r) => a + r.orders, 0)) },
              { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.netSales, 0)) },
              { key: 'grossSales', label: 'Incl Tax', num: true, render: r => money(r.grossSales), csv: r => (r.grossSales / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.grossSales, 0)) },
              {
                key: 'share', label: 'Share', num: true, noSort: true,
                render: r => c.netSales ? `${((r.netSales / c.netSales) * 100).toFixed(1)}%` : '—',
                csv: r => c.netSales ? ((r.netSales / c.netSales) * 100).toFixed(1) : '',
              },
            ]}
            rows={byOrderType}
            footer
          />
        </Card>

        <Card tour="sum-tenders" title="Payments by Tender" sub="amount collected incl tax, before tips · split payments attributed to their real tender">
          <DataTable
            csvName="payments-by-tender.csv"
            defaultSort={{ key: 'amount', dir: 'desc' }}
            columns={[
              { key: 'paymentType', label: 'Tender' },
              { key: 'payments', label: 'Payments', num: true, render: r => int(r.payments), csv: r => r.payments },
              { key: 'amount', label: 'Amount', num: true, render: r => money(r.amount), csv: r => (r.amount / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.amount, 0)) },
              { key: 'tips', label: 'Tips', num: true, render: r => money(r.tips), csv: r => (r.tips / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.tips, 0)) },
            ]}
            rows={byPaymentType}
            footer
          />
        </Card>
      </div>

      <Card title="Tender Mix" sub="amount collected by payment type">
        <div className="chart-box short">
          <Bar
            data={payBar}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: ctx => ` ${money(ctx.parsed.x)}` } },
              },
              scales: {
                x: { ticks: { callback: v => money(v, { cents: false }) }, beginAtZero: true },
                y: { grid: { display: false } },
              },
            }}
          />
        </div>
      </Card>

      <Card tour="sum-exceptions" title="Exceptions" sub="things worth a second look">
        <div className="grid kpi-grid">
          <KpiCard label="Failed / Cancelled Orders" value={int(c.failedOrders)} sub={`${money(c.failedAmount)} order value`} />
          <KpiCard label="Refunded" value={money(c.refundAmount)} sub={`${int(c.refundCount)} refunds`} />
          <KpiCard label="Order-level Discounts" value={money(c.discounts)} sub="see Discounts report for detail" />
          <KpiCard label="Added Charges" value={money(c.charges)} sub="delivery fees, service charges…" />
        </div>
      </Card>
    </div>
  );
}
